@if (hasAny) {
  <div class="sel">
    <h3 class="bk-eyebrow sel__title">Added so far</h3>

    <!-- Numbers typed in -->
    @if (phoneNumbers.length) {
      <section class="sel__group" aria-label="Phone numbers" data-group="numbers">
        <div class="sel__head">
          <p class="sel__label">Numbers <span class="sel__count">{{ formatCount(phoneNumbers.length) }}</span></p>
          @if (phoneNumbers.length > limit) {
            <button type="button" class="bk-more" (click)="handleOpenPhoneNumberModal()">
              See all <span class="bk-sym" aria-hidden="true">arrow_outward</span>
            </button>
          }
        </div>
        <ul class="sel__items">
          @for (phoneNumber of take(phoneNumbers, limit); track phoneNumber) {
            <li class="sel__item">
              <span class="sel__name">{{ phoneNumber }}</span>
              <button
                type="button"
                class="sel__remove"
                [attr.aria-label]="'Remove ' + phoneNumber"
                (click)="removePhoneNumber.emit(phoneNumber); keepFocus($event, 'numbers')"
              >
                <span class="bk-sym" aria-hidden="true">close</span>
              </button>
            </li>
          }
        </ul>
      </section>
    }

    <!-- Files -->
    @if (localCSVService.getAllFormattedFileData.length) {
      <section class="sel__group" aria-label="Uploaded files">
        <div class="sel__head">
          <p class="sel__label">
            Files <span class="sel__count">{{ localCSVService.getAllFormattedFileData.length }}</span>
          </p>
          @if (localCSVService.getAllFormattedFileData.length > fileLimit) {
            <button type="button" class="bk-more" (click)="handleOpenCSVSelectedFilesResultModal()">
              See all <span class="bk-sym" aria-hidden="true">arrow_outward</span>
            </button>
          } @else {
            <button type="button" class="bk-more" (click)="handleOpenCSVSelectedFilesResultModal()">
              Details <span class="bk-sym" aria-hidden="true">arrow_outward</span>
            </button>
          }
        </div>
        <ul class="sel__files">
          @for (file of take(localCSVService.getAllFormattedFileData, fileLimit); track file.batchId) {
            <li class="sel__file">
              <span class="bk-sym sel__file-icon" aria-hidden="true">description</span>
              <span class="sel__file-text">
                <span class="sel__file-name">{{ file.fileName || "Uploaded file" }}</span>
                <span class="sel__file-meta">
                  {{ formatCount(file.submitted.length) }} {{ file.submitted.length === 1 ? "number" : "numbers" }}
                  @if (file.duplicated.length) {
                    · {{ formatCount(file.duplicated.length) }} repeated {{ file.duplicated.length === 1 ? "number" : "numbers" }} merged
                  }
                </span>
              </span>
              <button
                type="button"
                class="bk-icon-btn"
                [attr.aria-label]="'Remove ' + (file.fileName || 'file')"
                (click)="removeFile(file.batchId)"
              >
                <span class="bk-sym" aria-hidden="true">close</span>
              </button>
            </li>
          }
        </ul>
      </section>
    }

    <!-- Contacts -->
    @if (selectedContacts.length) {
      <section class="sel__group" aria-label="Contacts" data-group="contacts">
        <div class="sel__head">
          <p class="sel__label">Contacts <span class="sel__count">{{ formatCount(selectedContacts.length) }}</span></p>
          @if (selectedContacts.length > limit) {
            <button type="button" class="bk-more" (click)="handleOpenContactsSelectedModal()">
              See all <span class="bk-sym" aria-hidden="true">arrow_outward</span>
            </button>
          }
        </div>
        <ul class="sel__items">
          @for (contact of take(selectedContacts, limit); track $index) {
            <li class="sel__item">
              <span class="sel__name">{{ contactName(contact) }}</span>
              <button
                type="button"
                class="sel__remove"
                [attr.aria-label]="'Remove ' + contactName(contact)"
                (click)="removeContact.emit(contact); keepFocus($event, 'contacts')"
              >
                <span class="bk-sym" aria-hidden="true">close</span>
              </button>
            </li>
          }
        </ul>
      </section>
    }

    <!-- Tags -->
    @if (selectedTags.length) {
      <section class="sel__group" aria-label="Tags" data-group="tags">
        <div class="sel__head">
          <p class="sel__label">Tags <span class="sel__count">{{ formatCount(selectedTags.length) }}</span></p>
          @if (selectedTags.length > limit) {
            <button type="button" class="bk-more" (click)="handleOpenTagsSelectedModal()">
              See all <span class="bk-sym" aria-hidden="true">arrow_outward</span>
            </button>
          }
        </div>
        <ul class="sel__items">
          @for (tag of take(selectedTags, limit); track $index) {
            <li class="sel__item">
              <span class="sel__name">{{ tag.name || "Unnamed tag" }}</span>
              <button
                type="button"
                class="sel__remove"
                [attr.aria-label]="'Remove ' + (tag.name || 'tag')"
                (click)="removeTag.emit(tag); keepFocus($event, 'tags')"
              >
                <span class="bk-sym" aria-hidden="true">close</span>
              </button>
            </li>
          }
        </ul>
      </section>
    }

    <!-- Organizations -->
    @if (selectedOrganizations.length) {
      <section class="sel__group" aria-label="Organizations" data-group="organizations">
        <div class="sel__head">
          <p class="sel__label">
            Organizations <span class="sel__count">{{ formatCount(selectedOrganizations.length) }}</span>
          </p>
          @if (selectedOrganizations.length > limit) {
            <button type="button" class="bk-more" (click)="handleOpenOrganizationsSelectedModal()">
              See all <span class="bk-sym" aria-hidden="true">arrow_outward</span>
            </button>
          }
        </div>
        <ul class="sel__items">
          @for (organization of take(selectedOrganizations, limit); track $index) {
            <li class="sel__item">
              <span class="sel__name">{{ organization.name || "Unnamed organization" }}</span>
              <button
                type="button"
                class="sel__remove"
                [attr.aria-label]="'Remove ' + (organization.name || 'organization')"
                (click)="removeOrganization.emit(organization); keepFocus($event, 'organizations')"
              >
                <span class="bk-sym" aria-hidden="true">close</span>
              </button>
            </li>
          }
        </ul>
      </section>
    }

    <!-- Segments -->
    @if (selectedSegments.length) {
      <section class="sel__group" aria-label="Segments" data-group="segments">
        <div class="sel__head">
          <p class="sel__label">Segments <span class="sel__count">{{ formatCount(selectedSegments.length) }}</span></p>
          @if (selectedSegments.length > limit) {
            <button type="button" class="bk-more" (click)="handleOpenSegmentsSelectedModal()">
              See all <span class="bk-sym" aria-hidden="true">arrow_outward</span>
            </button>
          }
        </div>
        <ul class="sel__items">
          @for (segment of take(selectedSegments, limit); track $index) {
            <li class="sel__item">
              <span class="sel__name">{{ segment.name || "Unnamed segment" }}</span>
              <button
                type="button"
                class="sel__remove"
                [attr.aria-label]="'Remove ' + (segment.name || 'segment')"
                (click)="removeSegment.emit(segment); keepFocus($event, 'segments')"
              >
                <span class="bk-sym" aria-hidden="true">close</span>
              </button>
            </li>
          }
        </ul>
      </section>
    }
  </div>
}
